/**
 * Client wrapper for the Offernest customer AI assistant endpoints.
 * All calls require an authenticated customer JWT — the shared `api` axios
 * instance injects the token automatically.
 */
import { api } from "@/lib/api";

const SESSION_STORAGE_KEY = "offernest_ai_session_id";

export const getStoredSessionId = () => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
        return null;
    }
};

export const setStoredSessionId = (id) => {
    try {
        if (id) sessionStorage.setItem(SESSION_STORAGE_KEY, id);
        else sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch { /* ignore */ }
};

export const clearStoredSession = () => setStoredSessionId(null);

/**
 * Submit a natural-language query. Returns
 *   { reply, cards, turn_id, meta }.
 * The `session_id` from meta must be persisted for the next call so the
 * server can stitch together conversation continuity.
 */
export const submitAIQuery = async ({ query, session_id, location, previous_turns }) => {
    const body = { query };
    if (session_id) body.session_id = session_id;
    if (location && typeof location.lat === "number" && typeof location.lng === "number") {
        body.location = { lat: location.lat, lng: location.lng };
    }
    if (previous_turns?.length) body.previous_turns = previous_turns.slice(-6);
    const res = await api.post("/customer/ai/query", body);
    return res.data;
};

/** Fetch the last 20 turns of a session (auth required, must own it). */
export const getAISession = async (session_id) => {
    if (!session_id) return { turns: [] };
    const res = await api.get(`/customer/ai/session/${encodeURIComponent(session_id)}`);
    return res.data;
};

/** Fire-and-forget thumbs up/down. Never throws — we swallow errors. */
export const sendAIFeedback = async ({ turn_id, card_index, helpful }) => {
    try {
        await api.post("/customer/ai/feedback", { turn_id, card_index, helpful });
        return true;
    } catch {
        return false;
    }
};
